import { HandType } from '../types/poker.types';

export enum VideoPokerVariant {
    JACKS_OR_BETTER = 'jacks_or_better',
    DEUCES_WILD = 'deuces_wild',
    BONUS_POKER = 'bonus_poker',
    DOUBLE_BONUS_POKER = 'double_bonus_poker'
}

export interface VideoPokerVariantConfig {
    id: string;
    name: string;
    description: string;
    paytableInfo: string;
    paytable: { [key in HandType]: number[] };
    specialRules: {
        wildCards?: number[]; // Wild card ranks (2 for deuces wild)
        qualifyingPairs?: number[]; // Minimum qualifying pair ranks
        bonusQuads?: { [key: number]: number[] }; // Bonus payouts for specific quads
    };
}

// Jacks or Better - Classic video poker
const JACKS_OR_BETTER_CONFIG: VideoPokerVariantConfig = {
    id: 'jacks_or_better',
    name: 'Jacks or Better',
    description: 'Classic video poker - pairs of Jacks or better qualify',
    paytableInfo: 'Royal: 250-4000 • Straight Flush: 50-250',
    paytable: {
        [HandType.ROYAL_FLUSH]: [250, 500, 750, 1000, 4000],
        [HandType.STRAIGHT_FLUSH]: [50, 100, 150, 200, 250],
        [HandType.FOUR_OF_A_KIND]: [25, 50, 75, 100, 125],
        [HandType.FULL_HOUSE]: [9, 18, 27, 36, 45],
        [HandType.FLUSH]: [6, 12, 18, 24, 30],
        [HandType.STRAIGHT]: [4, 8, 12, 16, 20],
        [HandType.THREE_OF_A_KIND]: [3, 6, 9, 12, 15],
        [HandType.TWO_PAIR]: [2, 4, 6, 8, 10],
        [HandType.PAIR]: [1, 2, 3, 4, 5],
        [HandType.HIGH_CARD]: [0, 0, 0, 0, 0],
        [HandType.FIVE_OF_A_KIND]: [0, 0, 0, 0, 0], // Not used in Jacks or Better
        [HandType.WILD_ROYAL_FLUSH]: [0, 0, 0, 0, 0], // Not used in Jacks or Better
        [HandType.FOUR_DEUCES]: [0, 0, 0, 0, 0] // Not used in Jacks or Better
    },
    specialRules: {
        qualifyingPairs: [11, 12, 13, 14] // Jacks, Queens, Kings, Aces
    }
};

// Deuces Wild - All 2s are wild
const DEUCES_WILD_CONFIG: VideoPokerVariantConfig = {
    id: 'deuces_wild',
    name: 'Deuces Wild',
    description: 'All 2s are wild cards - minimum Three of a Kind',
    paytableInfo: 'Natural Royal: 800-4000 • Four Deuces: 200-1000',
    paytable: {
        [HandType.ROYAL_FLUSH]: [800, 1600, 2400, 3200, 4000], // Natural royal
        [HandType.FOUR_DEUCES]: [200, 400, 600, 800, 1000], // Four deuces
        [HandType.WILD_ROYAL_FLUSH]: [25, 50, 75, 100, 125], // Wild royal
        [HandType.FIVE_OF_A_KIND]: [15, 30, 45, 60, 75], // Five of a kind
        [HandType.STRAIGHT_FLUSH]: [9, 18, 27, 36, 45], // Straight flush
        [HandType.FOUR_OF_A_KIND]: [5, 10, 15, 20, 25], // Four of a kind
        [HandType.FULL_HOUSE]: [3, 6, 9, 12, 15], // Full house
        [HandType.FLUSH]: [2, 4, 6, 8, 10], // Flush
        [HandType.STRAIGHT]: [2, 4, 6, 8, 10], // Straight
        [HandType.THREE_OF_A_KIND]: [1, 2, 3, 4, 5], // Three of a kind
        [HandType.TWO_PAIR]: [0, 0, 0, 0, 0], // Not paid in deuces wild
        [HandType.PAIR]: [0, 0, 0, 0, 0], // Not paid in deuces wild
        [HandType.HIGH_CARD]: [0, 0, 0, 0, 0]
    },
    specialRules: {
        wildCards: [2], // Deuces are wild
        qualifyingPairs: [] // No qualifying pairs - need three of a kind minimum
    }
};

// Bonus Poker - Bonus payouts for specific four of a kinds
const BONUS_POKER_CONFIG: VideoPokerVariantConfig = {
    id: 'bonus_poker',
    name: 'Bonus Poker',
    description: 'Bonus payouts for four Aces, 2s, 3s, and 4s',
    paytableInfo: 'Four Aces: 80-400 • Four 2s-4s: 40-200',
    paytable: {
        [HandType.ROYAL_FLUSH]: [250, 500, 750, 1000, 4000],
        [HandType.STRAIGHT_FLUSH]: [50, 100, 150, 200, 250],
        [HandType.FOUR_OF_A_KIND]: [25, 50, 75, 100, 125], // Base four of a kind
        [HandType.FULL_HOUSE]: [8, 16, 24, 32, 40],
        [HandType.FLUSH]: [5, 10, 15, 20, 25],
        [HandType.STRAIGHT]: [4, 8, 12, 16, 20],
        [HandType.THREE_OF_A_KIND]: [3, 6, 9, 12, 15],
        [HandType.TWO_PAIR]: [2, 4, 6, 8, 10],
        [HandType.PAIR]: [1, 2, 3, 4, 5],
        [HandType.HIGH_CARD]: [0, 0, 0, 0, 0],
        [HandType.FIVE_OF_A_KIND]: [0, 0, 0, 0, 0], // Not used
        [HandType.WILD_ROYAL_FLUSH]: [0, 0, 0, 0, 0], // Not used
        [HandType.FOUR_DEUCES]: [0, 0, 0, 0, 0] // Not used
    },
    specialRules: {
        qualifyingPairs: [11, 12, 13, 14], // Jacks or better
        bonusQuads: {
            14: [80, 160, 240, 320, 400], // Four Aces
            2: [40, 80, 120, 160, 200],   // Four 2s
            3: [40, 80, 120, 160, 200],   // Four 3s
            4: [40, 80, 120, 160, 200]    // Four 4s
        }
    }
};

// Double Bonus Poker - Higher bonus payouts, lower full house/flush
const DOUBLE_BONUS_POKER_CONFIG: VideoPokerVariantConfig = {
    id: 'double_bonus_poker',
    name: 'Double Bonus Poker',
    description: 'Higher quad bonuses, kicker-sensitive payouts',
    paytableInfo: 'Four Aces+2-4: 160-800 • Four Aces: 160-800',
    paytable: {
        [HandType.ROYAL_FLUSH]: [250, 500, 750, 1000, 4000],
        [HandType.STRAIGHT_FLUSH]: [50, 100, 150, 200, 250],
        [HandType.FOUR_OF_A_KIND]: [50, 100, 150, 200, 250], // Base four of a kind
        [HandType.FULL_HOUSE]: [9, 18, 27, 36, 45],
        [HandType.FLUSH]: [7, 14, 21, 28, 35],
        [HandType.STRAIGHT]: [5, 10, 15, 20, 25],
        [HandType.THREE_OF_A_KIND]: [3, 6, 9, 12, 15],
        [HandType.TWO_PAIR]: [1, 2, 3, 4, 5],
        [HandType.PAIR]: [1, 2, 3, 4, 5],
        [HandType.HIGH_CARD]: [0, 0, 0, 0, 0],
        [HandType.FIVE_OF_A_KIND]: [0, 0, 0, 0, 0], // Not used
        [HandType.WILD_ROYAL_FLUSH]: [0, 0, 0, 0, 0], // Not used
        [HandType.FOUR_DEUCES]: [0, 0, 0, 0, 0] // Not used
    },
    specialRules: {
        qualifyingPairs: [11, 12, 13, 14], // Jacks or better
        bonusQuads: {
            14: [160, 320, 480, 640, 800], // Four Aces
            2: [80, 160, 240, 320, 400],   // Four 2s
            3: [80, 160, 240, 320, 400],   // Four 3s
            4: [80, 160, 240, 320, 400],   // Four 4s
            5: [50, 100, 150, 200, 250],   // Four 5s-Kings
            6: [50, 100, 150, 200, 250],
            7: [50, 100, 150, 200, 250],
            8: [50, 100, 150, 200, 250],
            9: [50, 100, 150, 200, 250],
            10: [50, 100, 150, 200, 250],
            11: [50, 100, 150, 200, 250],
            12: [50, 100, 150, 200, 250],
            13: [50, 100, 150, 200, 250]
        }
    }
};

const VARIANT_CONFIGS: { [key in VideoPokerVariant]: VideoPokerVariantConfig } = {
    [VideoPokerVariant.JACKS_OR_BETTER]: JACKS_OR_BETTER_CONFIG,
    [VideoPokerVariant.DEUCES_WILD]: DEUCES_WILD_CONFIG,
    [VideoPokerVariant.BONUS_POKER]: BONUS_POKER_CONFIG,
    [VideoPokerVariant.DOUBLE_BONUS_POKER]: DOUBLE_BONUS_POKER_CONFIG
};

// Current variant management
let currentVariant: VideoPokerVariant = VideoPokerVariant.JACKS_OR_BETTER;

// Load from localStorage if available
const savedVariant = localStorage.getItem('selectedVideoPokerVariant') as VideoPokerVariant;
if (savedVariant && Object.values(VideoPokerVariant).includes(savedVariant)) {
    currentVariant = savedVariant;
}

export function getCurrentVariant(): VideoPokerVariantConfig {
    return VARIANT_CONFIGS[currentVariant];
}

export function setCurrentVariant(variant: VideoPokerVariant): void {
    currentVariant = variant;
    localStorage.setItem('selectedVideoPokerVariant', variant);
}

export function getVariantConfig(variant: VideoPokerVariant): VideoPokerVariantConfig {
    return VARIANT_CONFIGS[variant];
}

export function getAllVariants(): VideoPokerVariantConfig[] {
    return Object.values(VARIANT_CONFIGS);
}